import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    ClipTypeEnum,
    Engine,
    HighlightAnimationEnum,
    MainTextStyle, SubtitlesClip, SubtitlesManager,
    TextModeType,
} from '@rendley/sdk';
import {SubtitlesStylesDescription} from '../../../../config/config';
import {RendleyStore} from '../../../../store/RendleyStore';
import {ApplicationStore} from '../../../../store/ApplicationStore';
// Assuming these components are available as React components
import PanelGridListSection from "../../../../components/PanelGridListSection/PanelGridListSection";
import EffectShowcaseCard from "../../../../components/EffectsShowcaseCard/EffectShowcaseCard";
import {reaction} from "mobx";
import {ClipsToTimelineItems, GetSRTSubtitleUrl} from "../../../../../pages/ModernEditor/MultitrackTranscript";
import {RendleyService} from "../../../../services/RendleyService";
import {ToggleButton} from "../../../../../pages/NewStudio/ToggleButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {GenerateLibrettoClipId} from "../../../../Constants";

interface StyleConfiguration {
    name?: string;
    value: string;
    thumbnailUrl: string;
    videoPreviewUrl?: string;
}

// @ts-ignore
const SubtitlesStylesContainer = observer(() => {
    const [modes, setModes] = useState<StyleConfiguration[]>([]);
    const [animations, setAnimations] = useState<StyleConfiguration[]>([]);
    const [styles, setStyles] = useState<StyleConfiguration[]>([]);
    const [enabledDisabled, setEnabledDisabled] = useState<StyleConfiguration[]>([]);

    const [selectedMode, setSelectedMode] = useState(RendleyStore.subtitlesStyles.selectedMode);
    const [selectedStyle, setSelectedStyle] = useState(RendleyStore.subtitlesStyles.selectedStyle);
    const [selectedAnimation, setSelectedAnimation] = useState(RendleyStore.subtitlesStyles.selectedAnimation);

    const corrections = new Map();

    useEffect(() => {
        const disposer = reaction(
            () => ({
                selectedMode: RendleyStore.subtitlesStyles.selectedMode,
                selectedStyle: RendleyStore.subtitlesStyles.selectedStyle,
                selectedAnimation: RendleyStore.subtitlesStyles.selectedAnimation,
            }),
            ({selectedMode, selectedStyle, selectedAnimation}) => {
                setSelectedMode(selectedMode);
                setSelectedStyle(selectedStyle);
                setSelectedAnimation(selectedAnimation);
            });
        return () => {
            disposer(); // Dispose of the autorun when the component unmounts
        };
    }, []);

    useEffect(() => {
        init();
    }, []);

    const init = () => {
        const subtitleStylesRoot = "https://cdn.rendley.com/sdk/assets/subtitles/";

        fetch(subtitleStylesRoot + 'subtitles.json')
            .then((data) => data.json())
            .then((data: SubtitlesStylesDescription) => {
                setModes(
                    data.modes.map((mode) => ({
                        name: mode.name,
                        value: mode.value,
                        thumbnailUrl: subtitleStylesRoot + mode.path + 'thumbnail.webp',
                        videoPreviewUrl: subtitleStylesRoot + mode.path + 'preview.mp4',
                    }))
                );
                setAnimations(
                    data.animations.map((animation) => ({
                        name: animation.name,
                        value: animation.value,
                        thumbnailUrl: subtitleStylesRoot + animation.path + 'thumbnail.webp',
                        videoPreviewUrl: subtitleStylesRoot + animation.path + 'preview.mp4',
                    }))
                );
                setStyles(
                    data.styles.map((style) => ({
                        name: style.name,
                        value: subtitleStylesRoot + style.path + 'data.json',
                        thumbnailUrl: subtitleStylesRoot + style.path + 'thumbnail.webp',
                        videoPreviewUrl: subtitleStylesRoot + style.path + 'preview.mp4',
                    }))
                );
            });
        setEnabledDisabled([{
            name: 'Enabled',
            value: 'true',
            thumbnailUrl: '/on.png',
            videoPreviewUrl: '/on.png',
        }, {
            name: 'Disabled', value: 'false', thumbnailUrl: '/off.png',
            videoPreviewUrl: '/off.png',
        }]);
    };


    const handleUpdateSubtitlesDuration = () => {
        const clips = Engine.getInstance().getTimeline().getClips();

        // Update all clips associated with the subtitle id
        clips?.forEach((clip) => {
            if (clip.getType() === ClipTypeEnum.SUBTITLES) {
                (clip as SubtitlesClip).updateDuration();
            }
        });

        Engine.getInstance().getTimeline().adjustClipsLayout();
    }

    const handleSetMode = (value: StyleConfiguration) => {
        const subtitlesManager = Engine.getInstance().getSubtitlesManager();
        RendleyStore.setSubtitlesMode(value.value);
        subtitlesManager.setTextMode(value.value as TextModeType);
    };

    const handleSetEnabled = async (value: boolean) => {
        if (value) {
            const scriptLayerClipIds = RendleyStore.scriptLayerId
                ? RendleyStore.layers[RendleyStore.scriptLayerId].clipsIds
                : [];
            const scriptLayerClips = scriptLayerClipIds
                .map(id => RendleyStore.clips[id])
                .filter(clip => clip !== undefined);
            const timelineStateLayers = ClipsToTimelineItems(scriptLayerClips);
            const srtContent = GetSRTSubtitleUrl({timelineStateLayers: timelineStateLayers, corrections: corrections});
            const subtitlesClipId = await RendleyService.importSubtitlesSrt(srtContent);
            const subtitlesLibrettoClipId = GenerateLibrettoClipId();
            RendleyStore.addClipIdForLibrettoClip(subtitlesLibrettoClipId, subtitlesClipId);
            handleUpdateSubtitlesDuration();
            ApplicationStore.setSubtitlesLibrettoClipId(subtitlesLibrettoClipId);
            ApplicationStore.setSelectedLibrettoClipId(subtitlesLibrettoClipId);

            const subtitlesLayer = Engine.getInstance().getTimeline().getLayerByClipId(subtitlesClipId)

            RendleyStore.setLayerIdForLibrettoClip(subtitlesLibrettoClipId, subtitlesLayer.id);
            RendleyStore.addLibrettoClipIdToLayer(subtitlesLibrettoClipId, subtitlesLayer.id);
            return;
        }

        // Handle disabling - delete the subtitle clip
        if (ApplicationStore.subtitlesLibrettoClipId) {
            const clipIds = RendleyStore.getClipIdsForLibrettoClip(ApplicationStore.subtitlesLibrettoClipId);
            clipIds.forEach(clipId => {
                RendleyService.deleteClipById(clipId);
                RendleyStore.removeClipIdForLibrettoClip(ApplicationStore.subtitlesLibrettoClipId, clipId);
            });
            ApplicationStore.setSubtitlesLibrettoClipId(null);
            RendleyStore.setSubtitlesStyle(null);
        }
    }

    const handleSetStyle = async (value: StyleConfiguration) => {
        const subtitlesManager = Engine.getInstance().getSubtitlesManager();
        
        // If clicking the same style, unset it and disable subtitles
        if (value.value === RendleyStore.subtitlesStyles.selectedStyle) {
            RendleyStore.setSubtitlesStyle(null);
            const defaultStyles = {} as MainTextStyle;
            subtitlesManager.setMainTextStyle(defaultStyles, true);
            subtitlesManager.setHighlightedTextStyle(defaultStyles, true);
            
            // Disable subtitles (same as handleSetEnabled(false))
            await handleSetEnabled(false);
            return;
        }
        
        // If no subtitles exist yet, enable them first
        if (!ApplicationStore.subtitlesLibrettoClipId) {
            await handleSetEnabled(true);
        }
        
        // Apply the selected style
        RendleyStore.setSubtitlesStyle(value.value);
        const data = await fetch(value.value).then((data) => data.json());
        subtitlesManager.setMainTextStyle(data.mainTextStyle, true);
        subtitlesManager.setHighlightedTextStyle(data.highlightTextStyle, true);
    };

    const handleSetAnimation = (value: StyleConfiguration) => {
        const subtitlesManager = Engine.getInstance().getSubtitlesManager();
        if (value.value === RendleyStore.subtitlesStyles.selectedAnimation) {
            RendleyStore.setSubtitlesAnimation(null);
            subtitlesManager.setHighlightAnimation(HighlightAnimationEnum.NONE);
            return;
        }
        RendleyStore.setSubtitlesAnimation(value.value);
        subtitlesManager.setHighlightAnimation(
            value.value as HighlightAnimationEnum,
            1
        );
    };

    return (
        <div style={{width: '100%', maxHeight: '900px', height: "900px", minWidth: "250px", paddingBottom: "40px"}}>
            <PanelGridListSection
                title={'Styles'}
                data={styles}
                visibleItemsCount={styles.length}
                columns={1}
                renderCard={(style: StyleConfiguration) => (
                    <EffectShowcaseCard
                        key={style.value}
                        isSelected={RendleyStore.subtitlesStyles.selectedStyle === style.value}
                        // tooltip={style.name}
                        imageUrl={style.thumbnailUrl}
                        videoUrl={style.videoPreviewUrl}
                        onMouseDown={() => handleSetStyle(style)}
                    />
                )}
            />
            <div className="spacer-2"></div>
        </div>
    );
});

export default SubtitlesStylesContainer;
